import { useEffect, useState } from "react"
import { adminApi, type SiteSettings, type TelegramSettings } from "@/lib/admin-api"
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import { Switch } from "@/components/ui/switch"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { Settings, Bot, Upload, X, Loader2, CheckCircle, XCircle, Globe, Mail, Phone, MapPin, Instagram, Facebook, Send } from "lucide-react"
import { toast } from "sonner"

export default function AdminSettings() {
    const [isLoading, setIsLoading] = useState(true)
    const [isSaving, setIsSaving] = useState(false)
    const [isTesting, setIsTesting] = useState(false)

    // Site Settings
    const [siteSettings, setSiteSettings] = useState<SiteSettings | null>(null)
    const [siteFormData, setSiteFormData] = useState({
        site_name: "",
        site_description: "",
        email: "",
        phone: "",
        address: "",
        instagram: "",
        telegram: "",
        facebook: "",
        location_url: ""
    })
    const [logoFile, setLogoFile] = useState<File | null>(null)
    const [logoPreview, setLogoPreview] = useState<string | null>(null)

    // Telegram Settings
    const [telegramSettings, setTelegramSettings] = useState<TelegramSettings | null>(null)
    const [telegramFormData, setTelegramFormData] = useState({
        bot_token: "",
        channel_id: "",
        is_active: true
    })
    const [telegramTestResult, setTelegramTestResult] = useState<{ success: boolean; message: string } | null>(null)

    useEffect(() => {
        const fetchData = async () => {
            try {
                setIsLoading(true)
                const [site, telegram] = await Promise.all([
                    adminApi.getSiteSettings().catch(() => null),
                    adminApi.getTelegramSettings().catch(() => null)
                ])

                if (site) {
                    setSiteSettings(site)
                    setSiteFormData({
                        site_name: site.site_name || "",
                        site_description: site.site_description || "",
                        email: site.email || "",
                        phone: site.phone || "",
                        address: site.address || "",
                        instagram: site.instagram || "",
                        telegram: site.telegram || "",
                        facebook: site.facebook || "",
                        location_url: site.location_url || ""
                    })
                    if (site.logo_url) setLogoPreview(site.logo_url)
                }

                if (telegram) {
                    setTelegramSettings(telegram)
                    setTelegramFormData({
                        bot_token: telegram.bot_token || "",
                        channel_id: telegram.channel_id || "",
                        is_active: telegram.is_active ?? true
                    })
                }
            } catch (error) {
                toast.error("Sozlamalarni yuklashda xatolik")
            } finally {
                setIsLoading(false)
            }
        }
        fetchData()
    }, [])

    const handleLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0]
        if (file) {
            setLogoFile(file)
            setLogoPreview(URL.createObjectURL(file))
        }
    }

    const handleSaveSiteSettings = async () => {
        setIsSaving(true)
        try {
            const form = new FormData()
            Object.entries(siteFormData).forEach(([key, value]) => {
                form.append(key, value)
            })
            if (logoFile) {
                form.append("logo", logoFile)
            }
            await adminApi.updateSiteSettings(form)
            toast.success("Sayt sozlamalari saqlandi")
        } catch (error) {
            toast.error("Xatolik yuz berdi")
        } finally {
            setIsSaving(false)
        }
    }

    const handleSaveTelegramSettings = async () => {
        setIsSaving(true)
        try {
            await adminApi.updateTelegramSettings(telegramFormData)
            toast.success("Telegram sozlamalari saqlandi")
        } catch (error) {
            toast.error("Xatolik yuz berdi")
        } finally {
            setIsSaving(false)
        }
    }

    const handleTestTelegram = async () => {
        if (!telegramFormData.bot_token || !telegramFormData.channel_id) {
            toast.error("Bot token va kanal ID kiritilishi shart")
            return
        }
        setIsTesting(true)
        setTelegramTestResult(null)
        try {
            const result = await adminApi.testTelegramConnection({
                bot_token: telegramFormData.bot_token,
                channel_id: telegramFormData.channel_id
            })
            setTelegramTestResult(result)
            if (result.success) {
                toast.success("Telegram bot ishlayapti!")
            } else {
                toast.error(result.message || "Test muvaffaqiyatsiz")
            }
        } catch (error) {
            setTelegramTestResult({ success: false, message: "Server bilan bog'lanishda xatolik" })
            toast.error("Test muvaffaqiyatsiz")
        } finally {
            setIsTesting(false)
        }
    }

    if (isLoading) {
        return (
            <div className="flex items-center justify-center min-h-[60vh]">
                <div className="w-12 h-12 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin" />
            </div>
        )
    }

    return (
        <div className="space-y-6 max-w-4xl">
            <div>
                <h1 className="text-3xl font-bold">Sozlamalar</h1>
                <p className="text-muted-foreground">Sayt va integratsiyalar sozlamalari</p>
            </div>

            <Tabs defaultValue="site" className="space-y-6">
                <TabsList className="bg-slate-100 dark:bg-gray-800 p-1">
                    <TabsTrigger value="site" className="gap-2">
                        <Settings className="h-4 w-4" />
                        Sayt sozlamalari
                    </TabsTrigger>
                    <TabsTrigger value="telegram" className="gap-2">
                        <Bot className="h-4 w-4" />
                        Telegram Bot
                    </TabsTrigger>
                </TabsList>

                {/* Site Settings Tab */}
                <TabsContent value="site" className="space-y-6">
                    <Card>
                        <CardHeader>
                            <CardTitle>Asosiy ma'lumotlar</CardTitle>
                            <CardDescription>Sayt nomi va tavsifi</CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <div className="space-y-2">
                                <Label>Logo</Label>
                                <div className="flex items-center gap-4">
                                    {logoPreview ? (
                                        <div className="relative">
                                            <img src={logoPreview} alt="Logo" className="w-20 h-20 rounded-xl object-contain bg-slate-50" />
                                            <Button type="button" variant="destructive" size="icon" className="absolute -top-2 -right-2 h-6 w-6"
                                                onClick={() => { setLogoFile(null); setLogoPreview(null) }}>
                                                <X className="h-3 w-3" />
                                            </Button>
                                        </div>
                                    ) : (
                                        <label className="w-20 h-20 border-2 border-dashed rounded-xl flex items-center justify-center cursor-pointer hover:border-emerald-500">
                                            <Upload className="h-6 w-6 text-muted-foreground" />
                                            <input type="file" accept="image/*" onChange={handleLogoChange} className="hidden" />
                                        </label>
                                    )}
                                </div>
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label>Sayt nomi</Label>
                                    <Input value={siteFormData.site_name} onChange={(e) => setSiteFormData({ ...siteFormData, site_name: e.target.value })}
                                        placeholder="FahriDil Shop" />
                                </div>
                                <div className="space-y-2">
                                    <Label><Mail className="h-4 w-4 inline mr-1" />Email</Label>
                                    <Input type="email" value={siteFormData.email} onChange={(e) => setSiteFormData({ ...siteFormData, email: e.target.value })}
                                        placeholder="info@example.com" />
                                </div>
                            </div>
                            <div className="space-y-2">
                                <Label>Tavsif</Label>
                                <Textarea value={siteFormData.site_description} onChange={(e) => setSiteFormData({ ...siteFormData, site_description: e.target.value })}
                                    placeholder="Sayt haqida qisqa ma'lumot" rows={3} />
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label><Phone className="h-4 w-4 inline mr-1" />Telefon</Label>
                                    <Input value={siteFormData.phone} onChange={(e) => setSiteFormData({ ...siteFormData, phone: e.target.value })}
                                        placeholder="+998 90 123 45 67" />
                                </div>
                                <div className="space-y-2">
                                    <Label><MapPin className="h-4 w-4 inline mr-1" />Manzil</Label>
                                    <Input value={siteFormData.address} onChange={(e) => setSiteFormData({ ...siteFormData, address: e.target.value })}
                                        placeholder="Toshkent, O'zbekiston" />
                                </div>
                            </div>
                        </CardContent>
                    </Card>

                    <Card>
                        <CardHeader>
                            <CardTitle>Ijtimoiy tarmoqlar</CardTitle>
                            <CardDescription>Sayt uchun ijtimoiy tarmoq havolalari</CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label><Instagram className="h-4 w-4 inline mr-1" />Instagram</Label>
                                    <Input value={siteFormData.instagram} onChange={(e) => setSiteFormData({ ...siteFormData, instagram: e.target.value })}
                                        placeholder="https://instagram.com/..." />
                                </div>
                                <div className="space-y-2">
                                    <Label><Send className="h-4 w-4 inline mr-1" />Telegram</Label>
                                    <Input value={siteFormData.telegram} onChange={(e) => setSiteFormData({ ...siteFormData, telegram: e.target.value })}
                                        placeholder="https://t.me/..." />
                                </div>
                                <div className="space-y-2">
                                    <Label><Facebook className="h-4 w-4 inline mr-1" />Facebook</Label>
                                    <Input value={siteFormData.facebook} onChange={(e) => setSiteFormData({ ...siteFormData, facebook: e.target.value })}
                                        placeholder="https://facebook.com/..." />
                                </div>
                                <div className="space-y-2">
                                    <Label><Globe className="h-4 w-4 inline mr-1" />Joylashuv (xarita)</Label>
                                    <Input value={siteFormData.location_url} onChange={(e) => setSiteFormData({ ...siteFormData, location_url: e.target.value })}
                                        placeholder="Google Maps havolasi" />
                                </div>
                            </div>
                        </CardContent>
                    </Card>

                    <Button onClick={handleSaveSiteSettings} disabled={isSaving} className="bg-gradient-to-r from-emerald-500 to-teal-600 gap-2">
                        {isSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Settings className="h-4 w-4" />}
                        Saqlash
                    </Button>
                </TabsContent>

                {/* Telegram Settings Tab */}
                <TabsContent value="telegram" className="space-y-6">
                    <Card>
                        <CardHeader>
                            <CardTitle className="flex items-center gap-2">
                                <Bot className="h-5 w-5" />
                                Telegram Bot sozlamalari
                            </CardTitle>
                            <CardDescription>Yangi buyurtmalar haqida xabar olish uchun</CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <div className="space-y-2">
                                <Label>Bot Token</Label>
                                <Input value={telegramFormData.bot_token} onChange={(e) => setTelegramFormData({ ...telegramFormData, bot_token: e.target.value })}
                                    placeholder="123456789:ABCdefGHIjklMNOpqrsTUVwxyz" className="font-mono" />
                                <p className="text-xs text-muted-foreground">@BotFather dan oling</p>
                            </div>
                            <div className="space-y-2">
                                <Label>Kanal/Guruh ID</Label>
                                <Input value={telegramFormData.channel_id} onChange={(e) => setTelegramFormData({ ...telegramFormData, channel_id: e.target.value })}
                                    placeholder="-1001234567890 yoki @kanal_nomi" className="font-mono" />
                                <p className="text-xs text-muted-foreground">Kanal yoki guruh ID si</p>
                            </div>
                            <div className="flex items-center justify-between p-4 bg-slate-50 dark:bg-gray-800 rounded-lg">
                                <div>
                                    <p className="font-medium">Faol holat</p>
                                    <p className="text-sm text-muted-foreground">Xabar yuborishni yoqish/o'chirish</p>
                                </div>
                                <Switch checked={telegramFormData.is_active} onCheckedChange={(checked) => setTelegramFormData({ ...telegramFormData, is_active: checked })} />
                            </div>

                            {telegramTestResult && (
                                <div className={`p-4 rounded-lg flex items-center gap-3 ${telegramTestResult.success ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/20' : 'bg-red-50 text-red-700 dark:bg-red-900/20'}`}>
                                    {telegramTestResult.success ? <CheckCircle className="h-5 w-5" /> : <XCircle className="h-5 w-5" />}
                                    <p>{telegramTestResult.message}</p>
                                </div>
                            )}
                        </CardContent>
                    </Card>

                    <div className="flex gap-4">
                        <Button onClick={handleSaveTelegramSettings} disabled={isSaving} className="bg-gradient-to-r from-emerald-500 to-teal-600 gap-2">
                            {isSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                            Saqlash
                        </Button>
                        <Button variant="outline" onClick={handleTestTelegram} disabled={isTesting} className="gap-2">
                            {isTesting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                            Test xabar yuborish
                        </Button>
                    </div>
                </TabsContent>
            </Tabs>
        </div>
    )
}
